import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { pl } from 'date-fns/locale';
import {
  useCallback,
  useId,
  useMemo,
  useState,
  type KeyboardEvent,
} from 'react';
import type {
  DateRange,
  DayState,
  UseDatePickerOptions,
  UseDatePickerReturn,
} from './DatePickerModal.types';

// Week day short labels, Sunday-first (matches the current calendar column order)
const WEEK_DAY_LABELS = ['N', 'P', 'W', 'Ś', 'C', 'P', 'S'] as const;

function buildCalendarDays(month: Date): Date[] {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const calStart = startOfWeek(monthStart, { locale: pl });
  const calEnd = endOfWeek(monthEnd, { locale: pl });
  return eachDayOfInterval({ start: calStart, end: calEnd });
}

export function useDatePicker({
  isOpen,
  mode,
  selected,
  startDate,
  endDate,
  minDate,
  maxDate,
  startYear,
  endYear,
  onSelect,
  onRangeSelect,
  onClose,
}: UseDatePickerOptions): UseDatePickerReturn {
  const today = useMemo(() => startOfDay(new Date()), []);

  // Stable IDs for ARIA wiring
  const uid = useId();
  const headingId = `${uid}-heading`;
  const gridId = `${uid}-grid`;
  const dialogId = `${uid}-dialog`;

  const [currentMonth, setCurrentMonth] = useState<Date>(
    () => selected ?? startDate ?? today
  );
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [tempSelected, setTempSelected] = useState<Date | undefined>(() =>
    mode === 'single' ? (selected ?? today) : undefined
  );
  const [tempRange, setTempRange] = useState<DateRange>({
    start: startDate,
    end: endDate,
  });
  const [focusedDate, setFocusedDate] = useState<Date | undefined>(
    () => selected ?? startDate ?? today
  );

  // Synchronously reset temp state on every open so an outside-click dismiss
  // (which skips handleCancel) never leaves a stale selection visible.
  // Calling setState during render causes React to discard this render and
  // immediately re-render with the new state — no flash, no useEffect delay.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      setTempSelected(mode === 'single' ? (selected ?? today) : undefined);
      setTempRange({ start: startDate, end: endDate });
      setFocusedDate(selected ?? startDate ?? today);
      setCurrentMonth(selected ?? startDate ?? today);
    }
  }

  // ── Computed ──────────────────────────────────────────────────────────────

  const years = useMemo(
    () =>
      Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i),
    [startYear, endYear]
  );

  const calendarDays = useMemo(
    () => buildCalendarDays(currentMonth),
    [currentMonth]
  );

  const isDateDisabled = useCallback(
    (day: Date): boolean => {
      const d = startOfDay(day);
      if (minDate && isBefore(d, startOfDay(minDate))) {
        return true;
      }
      if (maxDate && isAfter(d, startOfDay(maxDate))) {
        return true;
      }
      return false;
    },
    [minDate, maxDate]
  );

  const canGoToPrevMonth = useMemo(() => {
    if (!minDate) {
      return true;
    }
    const prev = subMonths(startOfMonth(currentMonth), 1);
    return !isAfter(startOfMonth(minDate), startOfMonth(prev));
  }, [currentMonth, minDate]);

  const canGoToNextMonth = useMemo(() => {
    if (!maxDate) {
      return true;
    }
    const next = addMonths(startOfMonth(currentMonth), 1);
    return !isAfter(startOfMonth(next), startOfMonth(maxDate));
  }, [currentMonth, maxDate]);

  const monthLabel = useMemo(
    () => format(currentMonth, 'MMM yyyy', { locale: pl }),
    [currentMonth]
  );

  // ── Actions ───────────────────────────────────────────────────────────────

  const goToPrevMonth = useCallback(() => {
    if (!canGoToPrevMonth) {
      return;
    }
    setCurrentMonth((m) => subMonths(m, 1));
  }, [canGoToPrevMonth]);

  const goToNextMonth = useCallback(() => {
    if (!canGoToNextMonth) {
      return;
    }
    setCurrentMonth((m) => addMonths(m, 1));
  }, [canGoToNextMonth]);

  const toggleYearPicker = useCallback(() => setShowYearPicker((v) => !v), []);

  const selectYear = useCallback(
    (year: number) => {
      if (maxDate && year > maxDate.getFullYear()) {
        return;
      }
      const next = new Date(year, currentMonth.getMonth(), 1);
      if (maxDate && isAfter(startOfMonth(next), startOfMonth(maxDate))) {
        setCurrentMonth(startOfMonth(maxDate));
      } else if (
        minDate &&
        isBefore(startOfMonth(next), startOfMonth(minDate))
      ) {
        setCurrentMonth(startOfMonth(minDate));
      } else {
        setCurrentMonth(next);
      }
      setShowYearPicker(false);
    },
    [currentMonth, maxDate, minDate]
  );

  const handleDayClick = useCallback(
    (day: Date) => {
      const d = startOfDay(day);
      if (isDateDisabled(d)) {
        return;
      }
      setFocusedDate(d);
      if (mode === 'range') {
        setTempRange((prev) => {
          if (!prev.start || (prev.start && prev.end)) {
            return { start: d, end: undefined };
          }
          return isBefore(d, prev.start)
            ? { start: d, end: prev.start }
            : { start: prev.start, end: d };
        });
      } else {
        setTempSelected(d);
      }
    },
    [isDateDisabled, mode]
  );

  const handleOk = useCallback(() => {
    if (mode === 'range') {
      onRangeSelect?.(tempRange.start, tempRange.end);
    } else {
      onSelect(tempSelected);
    }
    onClose();
  }, [mode, onRangeSelect, onSelect, onClose, tempRange, tempSelected]);

  const handleCancel = useCallback(() => {
    setTempSelected(selected ?? today);
    setTempRange({ start: startDate, end: endDate });
    setFocusedDate(selected ?? startDate ?? today);
    onClose();
  }, [selected, startDate, endDate, today, onClose]);

  // ── Keyboard navigation (WAI-ARIA Date Picker Dialog pattern) ─────────────

  const handleGridKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTableElement>) => {
      if (!focusedDate) {
        return;
      }

      let next: Date | undefined;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          next = addDays(focusedDate, 1);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          next = addDays(focusedDate, -1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          next = addDays(focusedDate, 7);
          break;
        case 'ArrowUp':
          e.preventDefault();
          next = addDays(focusedDate, -7);
          break;
        case 'PageDown':
          e.preventDefault();
          next = addMonths(focusedDate, e.ctrlKey ? 12 : 1);
          break;
        case 'PageUp':
          e.preventDefault();
          next = addMonths(focusedDate, e.ctrlKey ? -12 : -1);
          break;
        case 'Home':
          e.preventDefault();
          // Move to first day of the visual week (Sunday-first layout)
          next = startOfWeek(focusedDate, { weekStartsOn: 0 });
          break;
        case 'End':
          e.preventDefault();
          next = endOfWeek(focusedDate, { weekStartsOn: 0 });
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!isDateDisabled(focusedDate)) {
            handleDayClick(focusedDate);
          }
          return;
        case 'Escape':
          e.preventDefault();
          handleCancel();
          return;
        default:
          return;
      }

      if (!next) {
        return;
      }
      const nextDay = startOfDay(next);
      // Skip disabled days on navigation
      if (isDateDisabled(nextDay)) {
        return;
      }
      setFocusedDate(nextDay);
      if (!isSameMonth(nextDay, currentMonth)) {
        setCurrentMonth(startOfMonth(nextDay));
      }
    },
    [focusedDate, isDateDisabled, handleDayClick, handleCancel, currentMonth]
  );

  // ── Derived day state ─────────────────────────────────────────────────────

  const getDayState = useCallback(
    (day: Date): DayState => {
      const d = startOfDay(day);
      let isSelected = false;
      let isInRange = false;
      let isRangeStart = false;
      let isRangeEnd = false;

      if (mode === 'range') {
        isRangeStart = !!tempRange.start && isSameDay(d, tempRange.start);
        isRangeEnd = !!tempRange.end && isSameDay(d, tempRange.end);
        isSelected = isRangeStart || isRangeEnd;
        isInRange =
          !!tempRange.start &&
          !!tempRange.end &&
          isAfter(d, tempRange.start) &&
          isBefore(d, tempRange.end);
      } else {
        isSelected = !!tempSelected && isSameDay(d, tempSelected);
      }

      return {
        date: day,
        isSelected,
        isInRange,
        isRangeStart,
        isRangeEnd,
        isToday: isSameDay(d, today),
        isCurrentMonth: isSameMonth(day, currentMonth),
        isDisabled: isDateDisabled(d),
        isFocused: !!focusedDate && isSameDay(d, focusedDate),
      };
    },
    [
      mode,
      tempSelected,
      tempRange,
      today,
      currentMonth,
      isDateDisabled,
      focusedDate,
    ]
  );

  return {
    currentMonth,
    calendarDays,
    years,
    weekDayLabels: WEEK_DAY_LABELS,
    showYearPicker,
    canGoToPrevMonth,
    canGoToNextMonth,
    monthLabel,
    tempSelected,
    tempRange,
    focusedDate,
    headingId,
    gridId,
    dialogId,
    goToPrevMonth,
    goToNextMonth,
    toggleYearPicker,
    selectYear,
    handleDayClick,
    handleGridKeyDown,
    handleOk,
    handleCancel,
    getDayState,
  };
}
